import store from "@/store";
import {
  reactive,
  ref,
  onBeforeMount,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";

import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useDynamicFields from "@/views/components/dynamic-fields/hooks/useDynamicFields";
import usePagination from "@/views/components/pagination/usePagination";

import { dynamicReport } from "@/configs/dynamic-config/dynamic-report";
import reportModel from "@/models/report/report";
import { dynamicTableModel } from "@/models/data-management/dynamic-table";

export default function useGenerateReport() {
  const vm = getCurrentInstance().proxy;

  const { openDefaultErrorModal } = useValidationModal();
  const { pagination, onPageChange } = usePagination();

  const { fetchReportListModel, generateReportModel } = reportModel();
  const { fetchQueryDisplayReportModel } = dynamicTableModel();
  const { resolveFields, buildInformationPayload } = useDynamicFields();

  const reportList = ref([]);
  const keyChange = ref(0);
  const selectedReport = reactive({ index: null, payload: null });
  const loading = reactive({ tableData: false, generateReport: false });

  const fields = [
    { key: "report_seq", label: "No." },
    { key: "report_name", label: "Report" },
    { key: "report_detail", label: "Detail" },
    { key: "actions", label: "" },
  ];

  const fetchReportList = () => {
    loading.tableData = true;
    let dateTimePicker = new Date();
    let nextDatePicker = new Date(dateTimePicker);
    nextDatePicker.setDate(dateTimePicker.getDate() + 1);
    nextDatePicker.toLocaleDateString();
    try {
      dynamicReport.filters[0].value =
        dateTimePicker.getFullYear() +
        "-" +
        (dateTimePicker.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        dateTimePicker.getDate() +
        " 00:00";
      dynamicReport.filters[1].value =
        nextDatePicker.getFullYear() +
        "-" +
        (nextDatePicker.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        nextDatePicker.getDate() +
        " 00:00";
    } catch {}
    fetchReportListModel.payload.page = pagination.currentPage;
    fetchReportListModel.payload.limit = pagination.perPage;
    store
      .dispatch("api/apiRequest", fetchReportListModel)
      .then((response) => {
        pagination.rows = response.data.resp.length;
        pagination.totalRows = response.data.filtered_record;
        response.data.resp.forEach(
          (report) => (report.filters = dynamicReport.filters)
        );

        response.data.resp.map((o) => {
          fetchDynamicFilter(o);
        });

        return (reportList.value = response.data.resp);
      })
      .catch((err) => {
        console.log(err);
        openDefaultErrorModal(err);
      })
      .finally(() => (loading.tableData = false));
  };

  const generateReport = async (report) => {
    loading.generateReport = true;

    try {
      const payload = await buildInformationPayload(report.filters);

      generateReportModel.payload.Report_Key = report.report_key;
      generateReportModel.payload.Report_Id = report.report_id;
      generateReportModel.payload.Filters = payload;

      await store.dispatch("api/apiRequest", generateReportModel);

      return vm.$emit("nextStep");
    } catch (err) {
      openDefaultErrorModal(err);
    } finally {
      loading.generateReport = false;
    }
  };

  const onSelectReport = (report, index) => {
    if (typeof selectedReport.index == "number") {
      selectedReport.payload.selected = false;
    }
    loading.generateReport = false;
    selectedReport.payload = report;
    selectedReport.index = index;
    selectedReport.payload.selected = true;
    keyChange.value++;
    return;
  };

  const fetchDynamicFilter = (itemObj) => {
    return new Promise((resolve) => {
      onMapDynamicFilter(itemObj.report_key).then((value) => {
        itemObj.filters = [...value];
      });
      resolve(itemObj);
    });
  };

  const onMapDynamicFilter = (reportKey) => {
    if (!reportKey) return;
    return new Promise((resolve) => {
      let dateTimePicker = new Date();
      let nextDatePicker = new Date(dateTimePicker);
      nextDatePicker.setDate(dateTimePicker.getDate() + 1);
      nextDatePicker.toLocaleDateString();

      let startDateISO =
        dateTimePicker.getFullYear() +
        "-" +
        (dateTimePicker.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        dateTimePicker.getDate() +
        " 00:00";

      let endDateISO =
        nextDatePicker.getFullYear() +
        "-" +
        (nextDatePicker.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        nextDatePicker.getDate() +
        " 00:00";

      try {
        dynamicReport.filters[0].value = startDateISO;
        dynamicReport.filters[1].value = endDateISO;
      } catch {}

      // default dynamic filter
      dynamicReport.filters[0].field_property.width = "auto";
      dynamicReport.filters[1].field_property.width = "auto";

      fetchQueryDisplayReportModel.payload.Display_Key = reportKey;
      store
        .dispatch("api/apiRequest", fetchQueryDisplayReportModel)
        .then((resp) => {
          let respData = resp.data.dynamic_fields;
          if (respData.length > 0) {
            try {
              respData[0].value = startDateISO;
              respData[1].value = endDateISO;
            } catch {}

            respData[0].field_property.width = "auto";
            respData[1].field_property.width = "auto";
          } else {
            respData = [...dynamicReport.filters];
          }
          resolve(respData);
        })
        .catch((err) => {
          openDefaultErrorModal(err);
        })
        .finally(() => {});
    });
  };

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchReportList();
        selectedReport.payload = null;
        selectedReport.index = null;
      }
    },
    { deep: true }
  );

  onBeforeMount(() => {
    fetchReportList();
  });

  return {
    fields,
    reportList,
    selectedReport,
    loading,
    keyChange,
    pagination,
    onPageChange,
    onSelectReport,
    generateReport,
    resolveFields,
  };
}
